import { DocumentTextIcon, SparklesIcon, FolderIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import { ScriptMode } from '@packages/global';

/**
 * ModeIndicator 组件属性
 */
export interface ModeIndicatorProps {
  /** 脚本模式 */
  mode: ScriptMode;
  /** 是否显示详细说明 */
  showDescription?: boolean;
  /** 自定义类名 */
  className?: string;
}

/**
 * 模式配置
 */
const MODE_CONFIG: Record<ScriptMode, {
  icon: typeof SparklesIcon;
  label: string;
  description: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  iconColor: string;
}> = {
  [ScriptMode.PROMPT]: {
    icon: SparklesIcon,
    label: '自由创作模式',
    description: 'AI 根据您的需求自由创作',
    bgColor: 'bg-primary/10',
    textColor: 'text-primary',
    borderColor: 'border-primary/30',
    iconColor: 'text-primary',
  },
  [ScriptMode.FILE_SCRIPT]: {
    icon: DocumentTextIcon,
    label: '文件剧本模式',
    description: '基于上传的脚本文件提取内容',
    bgColor: 'bg-info/10',
    textColor: 'text-info',
    borderColor: 'border-info/30',
    iconColor: 'text-info',
  },
  [ScriptMode.PROJECT_SCRIPT]: {
    icon: FolderIcon,
    label: '项目剧本模式',
    description: '基于项目剧本章节提取内容',
    bgColor: 'bg-success/10',
    textColor: 'text-success',
    borderColor: 'border-success/30',
    iconColor: 'text-success',
  },
  [ScriptMode.MV]: {
    icon: MusicalNoteIcon,
    label: 'MV 模式',
    description: '基于音频分析生成 MV 分镜',
    bgColor: 'bg-[#8B5CF6]/10',
    textColor: 'text-[#8B5CF6]',
    borderColor: 'border-[#8B5CF6]/30',
    iconColor: 'text-[#8B5CF6]',
  },
};

/**
 * ModeIndicator 组件
 * 
 * 显示当前脚本的生成模式
 * - PROMPT: AI 自由创作，橙色主题
 * - FILE_SCRIPT: 基于脚本文件提取，蓝色主题
 * - PROJECT_SCRIPT: 基于项目剧本章节，绿色主题
 * 
 * Requirements: 13.1, 13.2, 13.3
 */
export default function ModeIndicator({
  mode,
  showDescription = false,
  className = '',
}: ModeIndicatorProps) {
  const config = MODE_CONFIG[mode] || MODE_CONFIG[ScriptMode.PROMPT];
  const Icon = config.icon;

  return (
    <div
      className={`
        inline-flex items-center gap-2 px-3 py-1.5 rounded-full
        border ${config.bgColor} ${config.borderColor}
        ${className}
      `}
    >
      <Icon className={`w-4 h-4 ${config.iconColor}`} />
      <span className={`text-xs font-medium ${config.textColor}`}>
        {config.label}
      </span>
      {showDescription && (
        <span className="text-xs text-gray-400 ml-1">
          - {config.description}
        </span>
      )}
    </div>
  );
}

/**
 * ModeIndicatorCompact 组件
 * 紧凑版本，仅显示图标和标签
 */
export function ModeIndicatorCompact({
  mode,
  className = '',
}: Omit<ModeIndicatorProps, 'showDescription'>) {
  const config = MODE_CONFIG[mode] || MODE_CONFIG[ScriptMode.PROMPT];
  const Icon = config.icon;

  return (
    <div
      className={`
        inline-flex items-center gap-1.5 px-2 py-1 rounded
        ${config.bgColor}
        ${className}
      `}
    >
      <Icon className={`w-3.5 h-3.5 ${config.iconColor}`} />
      <span className={`text-xs font-medium ${config.textColor}`}>
        {config.label}
      </span>
    </div>
  );
}

/**
 * ModeIndicatorBadge 组件
 * 徽章版本，仅显示图标
 */
export function ModeIndicatorBadge({
  mode,
  className = '',
}: Omit<ModeIndicatorProps, 'showDescription'>) {
  const config = MODE_CONFIG[mode] || MODE_CONFIG[ScriptMode.PROMPT];
  const Icon = config.icon;

  return (
    <div
      className={`
        inline-flex items-center justify-center w-6 h-6 rounded-full
        ${config.bgColor}
        ${className}
      `}
      title={config.label}
    >
      <Icon className={`w-3.5 h-3.5 ${config.iconColor}`} />
    </div>
  );
}
